import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { i18n } from "../../translate/i18n";
import { capitalize } from "../../helpers/terminologyGrammar";

const turnosApiUrl =
  window.ENV?.VITE_TURNOS_URL ||
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_TURNOS_URL) ||
  "https://apiwaia.zzia.info/turnosIA";

const LOCALE_MAP = { es: "es-AR", it: "it-IT", en: "en-US" };

// ─── Audio: ding dong con Web Audio API ──────────────────────────────────────
const playDingDong = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playTone = (freq, startTime, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.4, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    playTone(880, ctx.currentTime, 0.35);
    playTone(660, ctx.currentTime + 0.4, 0.4);
    setTimeout(() => ctx.close(), 1200);
  } catch (e) {
    console.warn("Audio no disponible:", e);
  }
};

// ─── Voz con Web Speech API ──────────────────────────────────────────────────
const speakText = (text, lang = "es") => {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = LOCALE_MAP[lang] || "es-AR";
  utt.rate = 0.9;
  utt.pitch = 1;
  utt.volume = 1;
  window.speechSynthesis.speak(utt);
};

const renderVoiceTemplate = (template, vars) => {
  if (!template) return "";
  return template.replace(/\{(\w+)\}/g, (_, key) => vars[key] || `{${key}}`);
};

// ─── Estilos inline (dark theme TV) ──────────────────────────────────────────
const styles = {
  root: {
    minHeight: "100vh",
    backgroundColor: "#0d1117",
    color: "#e6edf3",
    fontFamily: "'Segoe UI', 'Roboto', sans-serif",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 32px",
    borderBottom: "1px solid #21262d",
    background: "rgba(22, 27, 34, 0.95)"
  },
  roomName: {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#58a6ff",
    letterSpacing: 1
  },
  clock: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#e6edf3",
    fontVariantNumeric: "tabular-nums",
    fontFamily: "monospace"
  },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "24px 32px",
    gap: 24
  },
  attendedSection: {
    flex: "0 0 55%",
    display: "flex",
    flexDirection: "column"
  },
  sectionLabel: {
    fontSize: "0.85rem",
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: "#8b949e",
    marginBottom: 12
  },
  attendedGrid: {
    display: "flex",
    gap: 20,
    flexWrap: "wrap"
  },
  attendedCard: {
    flex: "1 1 340px",
    background: "linear-gradient(135deg, #161b22 0%, #1c2128 100%)",
    border: "2px solid #30363d",
    borderRadius: 16,
    padding: "28px 32px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    animation: "fadeIn 0.5s ease-in-out"
  },
  attendedCardActive: {
    borderColor: "#238636",
    boxShadow: "0 0 0 1px #238636, 0 8px 32px rgba(35,134,54,0.2)"
  },
  // "Llamado" (pasando a la sala, aún no arrancó) — tinte ámbar distinto del
  // verde de "en atención".
  attendedCardCalled: {
    borderColor: "#d29922",
    boxShadow: "0 0 0 1px #d29922, 0 8px 32px rgba(210,153,34,0.2)"
  },
  statusBadge: {
    position: "absolute",
    top: 12,
    right: 16,
    fontSize: "0.85rem",
    fontWeight: 800,
    padding: "2px 12px",
    borderRadius: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5
  },
  waitBadge: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: "#3fb950",
    background: "#0d2818",
    padding: "2px 10px",
    borderRadius: 10,
    whiteSpace: "nowrap"
  },
  patientName: {
    fontSize: "3.5rem",
    fontWeight: 900,
    color: "#ffffff",
    lineHeight: 1.1,
    marginBottom: 12,
    textTransform: "uppercase",
    letterSpacing: 1
  },
  arrow: {
    fontSize: "1.8rem",
    color: "#238636",
    marginBottom: 4
  },
  consultorioText: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#3fb950",
    marginBottom: 8
  },
  professionalText: {
    fontSize: "1.2rem",
    color: "#8b949e",
    fontWeight: 400
  },
  emptyAttended: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px dashed #30363d",
    borderRadius: 16,
    minHeight: 180,
    color: "#484f58",
    fontSize: "1.4rem",
    fontStyle: "italic"
  },
  bottomSection: {
    flex: "0 0 40%",
    display: "flex",
    gap: 24
  },
  bottomPanel: {
    flex: 1,
    background: "#161b22",
    borderRadius: 12,
    border: "1px solid #21262d",
    padding: "16px 20px",
    overflow: "hidden"
  },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #21262d",
    gap: 12
  },
  bottomName: {
    flex: 1,
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#c9d1d9"
  },
  bottomProf: {
    fontSize: "0.9rem",
    color: "#8b949e"
  },
  recentRow: {
    opacity: 0.55
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    flexDirection: "column",
    gap: 16
  }
};

const injectStyles = () => {
  if (document.getElementById("sala-espera-styles")) return;
  const style = document.createElement("style");
  style.id = "sala-espera-styles";
  style.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .attended-card-new {
      animation: fadeIn 0.6s ease-in-out;
    }
  `;
  document.head.appendChild(style);
};

const SalaEspera = ({ companySlug: propCompanySlug, roomSlug: propRoomSlug, language: propLanguage }) => {
  const params = useParams();
  const companySlug = propCompanySlug || params.companySlug;
  const roomSlug = propRoomSlug || params.roomSlug;

  const [lang, setLang] = useState(propLanguage || "es");
  const [roomConfig, setRoomConfig] = useState(null);
  // Vocabulario de la empresa (viene del endpoint público, ver
  // WaitingRoomController.getRoomData). {} = empresa vieja / error → los
  // textos caen al i18n de la pantalla, como antes.
  const [vocab, setVocab] = useState({});
  const [vocabLocale, setVocabLocale] = useState(null);
  const [currentlyAttended, setCurrentlyAttended] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clock, setClock] = useState(new Date());
  const prevAttendedRef = useRef([]);

  // Palabra del vocabulario de la empresa. Sin dato → el default por idioma
  // que ya vive en el i18n de la UI (turnosIA.vocabDefaults).
  const tt = useCallback((key) => (
    vocab[key] || i18n.getFixedT(lang)(`turnosIA.vocabDefaults.${key}`) || ""
  ), [vocab, lang]);

  // Local translation function — does not modify global i18n state.
  // Interpola el vocabulario: los textos de esta pantalla ya no dicen
  // "consultorio" ni "turnos" fijos, dicen lo que diga la empresa.
  const tSala = useCallback((key, extra) => {
    const w = k => vocab[k] || i18n.getFixedT(lang)(`turnosIA.vocabDefaults.${k}`) || "";
    const vars = {
      waitingRoom: w("waitingRoom"), waitingRoom_c: capitalize(w("waitingRoom")),
      room: w("room"), room_c: capitalize(w("room")),
      patient: w("patient"), patientPlural: w("patientPlural"),
      professional: w("professional"), professionalPlural: w("professionalPlural"),
      appointment: w("appointment"), appointmentPlural: w("appointmentPlural"),
      appointmentPlural_c: capitalize(w("appointmentPlural")),
      ...(extra || {})
    };
    return i18n.getFixedT(lang)(`salaEspera.${key}`, vars);
  }, [lang, vocab]);

  useEffect(() => {
    if (propLanguage) setLang(propLanguage);
  }, [propLanguage]);

  // Sin idioma explícito en la URL corta, la pantalla pública toma el locale de
  // la EMPRESA (el mismo con el que se resolvió su vocabulario). Antes quedaba
  // siempre en "es", incluso para una empresa italiana.
  useEffect(() => {
    if (!propLanguage && vocabLocale) setLang(vocabLocale);
  }, [propLanguage, vocabLocale]);

  useEffect(() => {
    injectStyles();
    const tick = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  const fetchData = useCallback(async (isInit = false) => {
    const endpoint = isInit
      ? `${turnosApiUrl}/public/waiting-room/${companySlug}/${roomSlug}`
      : `${turnosApiUrl}/public/waiting-room/${companySlug}/${roomSlug}/poll`;

    try {
      const res = await fetch(endpoint, { cache: "no-store" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `Error ${res.status}`);
        return;
      }
      const data = await res.json();

      if (isInit) {
        setRoomConfig(data.room);
        setVocab(data.terminology || {});
        setVocabLocale(data.locale || null);
        setCurrentlyAttended(data.currentlyAttended || []);
        setUpcoming(data.upcoming || []);
        setRecent(data.recent || []);
        prevAttendedRef.current = data.currentlyAttended || [];
        setLoading(false);
      } else {
        const newAttended = data.currentlyAttended || [];
        const prev = prevAttendedRef.current;

        const changed = newAttended.some(a =>
          !prev.find(p => p.appointmentId === a.appointmentId)
        );

        if (changed && roomConfig) {
          if (roomConfig.soundEnabled) {
            playDingDong();
          }
          if (roomConfig.voiceEnabled && roomConfig.voiceTemplate && newAttended.length > 0) {
            const apt = newAttended[0];
            const text = renderVoiceTemplate(roomConfig.voiceTemplate, {
              nombre: apt.patientDisplay,
              profesional: apt.professionalName,
              consultorio: apt.consultorio || tt("room")
            });
            setTimeout(() => speakText(text, lang), 800);
          }
        }

        prevAttendedRef.current = newAttended;
        setCurrentlyAttended(newAttended);
        setUpcoming(data.upcoming || []);
        setRecent(data.recent || []);
      }
    } catch (err) {
      if (isInit) setError(tSala("errorConexion"));
    }
  }, [companySlug, roomSlug, roomConfig, lang, tSala]);

  useEffect(() => {
    fetchData(true);
  }, [companySlug, roomSlug]);

  useEffect(() => {
    if (loading) return;
    const interval = setInterval(() => fetchData(false), 10000);
    return () => clearInterval(interval);
  }, [loading, fetchData]);

  const formatClock = (d) =>
    d.toLocaleTimeString(LOCALE_MAP[lang] || "es-AR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  if (error) {
    return (
      <div style={{ ...styles.root, ...styles.errorBox }}>
        <span style={{ fontSize: "4rem" }}>⚠️</span>
        <p style={{ fontSize: "1.5rem", color: "#f85149" }}>{error}</p>
        <p style={{ color: "#8b949e", fontSize: "0.9rem" }}>
          /sala/{companySlug}/{roomSlug}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ ...styles.root, ...styles.errorBox }}>
        <div style={{ width: 48, height: 48, border: "4px solid #238636", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
        <p style={{ color: "#8b949e" }}>{tSala("cargando")}</p>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.roomName}>
          {roomConfig?.name || tSala("fallbackNombre")}
        </div>
        <div style={styles.clock}>{formatClock(clock)}</div>
      </div>

      <div style={styles.body}>
        {/* Zona superior: en atención */}
        <div style={styles.attendedSection}>
          <div style={styles.sectionLabel}>{tSala("enAtencion")}</div>
          <div style={styles.attendedGrid}>
            {currentlyAttended.length === 0 ? (
              <div style={styles.emptyAttended}>
                {tSala("aguardando")}
              </div>
            ) : (
              currentlyAttended.map((apt) => {
                const isCalled = apt.status === "called";
                return (
                  <div
                    key={apt.appointmentId}
                    style={{
                      ...styles.attendedCard,
                      ...(isCalled ? styles.attendedCardCalled : styles.attendedCardActive),
                      position: "relative"
                    }}
                    className="attended-card-new"
                  >
                    <div
                      style={{
                        ...styles.statusBadge,
                        background: isCalled ? "#3a2d0b" : "#0d2818",
                        color: isCalled ? "#e3b341" : "#3fb950"
                      }}
                    >
                      {isCalled ? tSala("llamado") : tSala("enAtencionCard")}
                    </div>
                    <div style={styles.patientName}>{apt.patientDisplay}</div>
                    <div style={{ ...styles.arrow, color: isCalled ? "#d29922" : "#238636" }}>→</div>
                    <div style={{ ...styles.consultorioText, color: isCalled ? "#e3b341" : "#3fb950" }}>
                      {apt.consultorio || capitalize(tt("room"))}
                    </div>
                    <div style={styles.professionalText}>
                      {apt.professionalName}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Zona inferior: próximos + atendidos */}
        <div style={styles.bottomSection}>
          {/* Próximos */}
          {roomConfig?.showUpcoming !== false && (
            <div style={styles.bottomPanel}>
              <div style={{ ...styles.sectionLabel, marginBottom: 8 }}>
                {tSala("proximosTurnos")}
              </div>
              {upcoming.length === 0 ? (
                <p style={{ color: "#484f58", fontStyle: "italic", fontSize: "0.9rem" }}>{tSala("sinProximos")}</p>
              ) : (
                upcoming.slice(0, 6).map(apt => {
                  let waitLabel = null;
                  if (roomConfig?.showEstimatedWait && apt.estimatedWaitMinutes != null) {
                    const min = apt.estimatedStartAt
                      ? Math.max(0, Math.round((new Date(apt.estimatedStartAt).getTime() - clock.getTime()) / 60000))
                      : apt.estimatedWaitMinutes;
                    waitLabel = `~${min} ${tSala("min")}`;
                  }
                  return (
                    <div key={apt.appointmentId} style={styles.bottomRow}>
                      <div style={styles.bottomName}>{apt.patientDisplay}</div>
                      <div style={styles.bottomProf}>{apt.professionalName}</div>
                      {waitLabel && <div style={styles.waitBadge}>{waitLabel}</div>}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Atendidos */}
          {roomConfig?.showPrevious !== false && (
            <div style={styles.bottomPanel}>
              <div style={{ ...styles.sectionLabel, marginBottom: 8 }}>
                {tSala("yaAtendidos")}
              </div>
              {recent.length === 0 ? (
                <p style={{ color: "#484f58", fontStyle: "italic", fontSize: "0.9rem" }}>{tSala("sinPrevios")}</p>
              ) : (
                recent.slice(0, 6).map(apt => (
                  <div key={apt.appointmentId} style={{ ...styles.bottomRow, ...styles.recentRow }}>
                    <div style={styles.bottomName}>{apt.patientDisplay}</div>
                    <div style={styles.bottomProf}>{apt.professionalName}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalaEspera;
